import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { create } from "zustand";
import { useApi } from "~/api/provider";
import { useCurrentWorkspace } from "~/api/queries";

export interface PileCard {
  id: string;
  name: string;
  version: number;
  updatedAt: string;
}

export interface DocumentPile extends PileCard {
  documents: { id: string; title: string; updatedAt: string }[];
  unavailable: number;
  queries: string[];
  limited: boolean;
  condensed: boolean;
  summary: string | null;
  summaryLimited: boolean;
  turns: { id: string; mode: string; question: string; answer: string; at: string }[];
  published: { collectionId: string; documentId: string } | null;
}

export interface ThinkerAnswer {
  answer: string | null;
  pile: DocumentPile | null;
  limited: boolean;
}

export const useThinkerDock = create<{
  open: boolean;
  prefill: string;
  section: "conversation" | "decisions";
  sectionRequest: number;
  showDecisions: () => void;
  show: (prefill?: string) => void;
  close: () => void;
}>((set) => ({
  open: false,
  prefill: "",
  section: "conversation",
  sectionRequest: 0,
  showDecisions: () =>
    set((s) => ({ open: true, section: "decisions", sectionRequest: s.sectionRequest + 1 })),
  show: (prefill = "") =>
    set((s) => ({
      open: true,
      prefill,
      section: "conversation",
      sectionRequest: s.sectionRequest + 1,
    })),
  close: () => set({ open: false }),
}));

function usePileRpc() {
  const api = useApi();
  return async <T>(path: string, body: unknown = {}): Promise<T> => {
    const response = (await api.research.post(path, body)) as {
      ok: boolean;
      data: T;
      message?: string;
    };
    if (!response.ok) throw new Error(response.message ?? "The request could not be completed.");
    return response.data;
  };
}

export function usePiles() {
  const rpc = usePileRpc();
  const { data: workspace } = useCurrentWorkspace();
  return useQuery({
    queryKey: ["work", "piles", workspace?.id],
    queryFn: () => rpc<PileCard[]>("work.piles.list"),
    enabled: !!workspace,
  });
}

export function usePile(id: string | null) {
  const rpc = usePileRpc();
  const { data: workspace } = useCurrentWorkspace();
  return useQuery({
    queryKey: ["work", "piles", workspace?.id, id],
    queryFn: () => rpc<DocumentPile>("work.piles.get", { id }),
    enabled: !!id && !!workspace,
  });
}

export function useAskWorkspaceThinker() {
  const rpc = usePileRpc();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (prompt: string) => rpc<ThinkerAnswer>("workspace-thinker.ask", { prompt }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["work", "piles"] });
    },
  });
}

export function usePileAction(pile: DocumentPile) {
  const rpc = usePileRpc();
  const client = useQueryClient();
  const { data: workspace } = useCurrentWorkspace();
  return useMutation({
    mutationFn: ({
      action,
      ...body
    }: { action: "update" | "think" | "condense" | "publish" } & Record<string, unknown>) =>
      rpc<DocumentPile>(`work.piles.${action}`, { ...body, id: pile.id, version: pile.version }),
    onSuccess: (next) => {
      client.setQueryData(["work", "piles", workspace?.id, pile.id], next);
      void client.invalidateQueries({ queryKey: ["work", "piles"] });
      void client.invalidateQueries({ queryKey: ["collections"] });
      void client.invalidateQueries({ queryKey: ["documents"] });
    },
    onError: () => {
      void client.invalidateQueries({ queryKey: ["work", "piles"] });
    },
  });
}
